import CoinIcon from '../components/CoinIcon';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { FiCalendar, FiMapPin, FiUser, FiInfo, FiShare2, FiCheck } from 'react-icons/fi';

const THEME_COLORS = {
  elegant: { bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
  gold: { bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
  traditional: { bg: '#8B2252', accent: '#F2C4D6', text: '#FFF8F0' },
  modern: { bg: '#0F0F0F', accent: '#FFFFFF', text: '#FFFFFF' },
  floral: { bg: '#2D1B2E', accent: '#F0B6C5', text: '#FFF8F0' },
  minimal: { bg: '#FFF5F8', accent: '#C97191', text: '#2D1F2B' },
};

function isLightColor(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

function formatZAR(amount) {
  if (amount == null || isNaN(amount)) return 'R0';
  return 'R' + Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export default function TicketPurchase() {
  const { shareLink } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('select'); // select | details | confirm | success
  const [selectedTier, setSelectedTier] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '' });
  const [purchasedTickets, setPurchasedTickets] = useState([]); // [{ticket_code, tier_name, price_paid, guest_name}]
  const [purchaseEventTitle, setPurchaseEventTitle] = useState('');
  const [processingPayment, setProcessingPayment] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ozow') === 'success' && !!params.get('ref');
  });

  useEffect(() => {
    if (!shareLink || shareLink === 'null' || shareLink === 'undefined') {
      setError('Invalid ticket link. The event host may not have set up ticketing yet.');
      setLoading(false);
      return;
    }
    api.get(`/public/events/${shareLink}/tickets`)
      .then(r => {
        setEvent(r.data.event);
        setTiers(r.data.tiers || []);
      })
      .catch(err => { setError(err.response?.data?.error || 'Event not found'); })
      .finally(() => setLoading(false));
  }, [shareLink]);

  // Check for Ozow return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ozowRef = params.get('ref');
    const ozowOk = params.get('ozow') === 'success';

    if (ozowRef && ozowOk) {
      // Ozow EFT return — verify ticket creation
      const pending = sessionStorage.getItem('ggr_ozow_ticket_pending');
      if (pending) {
        const data = JSON.parse(pending);
        sessionStorage.removeItem('ggr_ozow_ticket_pending');
        setSubmitting(true);
        api.post('/ozow/ticket/verify', { reference: ozowRef }).then(r => {
          setPurchaseEventTitle(r.data.event_title || event?.title || '');
          const codes = r.data.ticket_codes || [r.data.ticket_code] || [];
          const tierName = r.data.tier_name || selectedTierData?.name || '';
          const pricePaid = r.data.amount_paid || 0;
          const guestName = r.data.guest_name || data.buyer_name || 'Guest';
          setPurchasedTickets(codes.map(code => ({
            ticket_code: code,
            tier_name: tierName,
            price_paid: pricePaid,
            guest_name: guestName
          })));
          setStep('success');
          setSubmitting(false);
          setProcessingPayment(false);
          // Auto-RSVP the ticket buyer
          api.post(`/invite/${shareLink}/rsvp`, {
            name: data.buyer_name || '',
            email: data.buyer_email,
            phone: data.buyer_phone || ''
          }).catch(() => {});
        }).catch(err => {
          setError(err.response?.data?.error || 'We\'re confirming your Ozow payment. Please refresh in a moment or check your tickets shortly.');
          setSubmitting(false);
          setProcessingPayment(false);
        });
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A1A2E' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #E8A0BF33', borderTop: '3px solid #E8A0BF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (processingPayment || submitting) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A1A2E', padding: '20px' }}>
        <div style={{ textAlign: 'center', maxWidth: '380px' }}>
          <div style={{ width: '48px', height: '48px', border: '3px solid rgba(232,160,191,0.15)', borderTop: '3px solid #E8A0BF', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
          <h2 style={{ color: '#FFF5F8', fontSize: '24px', fontFamily: "'Playfair Display', serif", marginBottom: '8px' }}>Processing Payment</h2>
          <p style={{ color: '#FFF5F8', opacity: 0.6, fontFamily: 'Inter, sans-serif', lineHeight: 1.5, margin: 0 }}>Please wait while we confirm your payment. Do not close this page.</p>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A1A2E', padding: '20px' }}>
        <div style={{ textAlign: 'center', color: '#FFF5F8', maxWidth: '420px' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🎫</div>
          <h1 style={{ fontSize: '28px', marginBottom: '12px', fontFamily: "'Playfair Display', serif" }}>Not Available</h1>
          <p style={{ opacity: 0.6, fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>{error}</p>
          <p style={{ marginTop: '24px', fontSize: '13px', opacity: 0.4 }}>If you believe this is a mistake, please contact the event host.</p>
        </div>
      </div>
    );
  }

  if (!event || tiers.length === 0) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A1A2E', padding: '20px' }}>
        <div style={{ textAlign: 'center', color: '#FFF5F8', maxWidth: '420px' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🎫</div>
          <h1 style={{ fontSize: '28px', marginBottom: '12px', fontFamily: "'Playfair Display', serif" }}>No Tickets Available</h1>
          <p style={{ opacity: 0.6, fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>This event doesn't have any active ticket tiers yet. Check back later or contact the host.</p>
        </div>
      </div>
    );
  }

  const customColors = event.colors && typeof event.colors === 'object' && event.colors.bg ? event.colors : null;
  const colors = event.theme === 'custom' && customColors ? customColors : (THEME_COLORS[event.theme] || THEME_COLORS.elegant);
  const isLight = event.theme === 'minimal' || (customColors && isLightColor(customColors.bg));
  const eventDate = event.event_date ? new Date(event.event_date) : null;
  const ticketDeadlineDate = event.ticket_deadline ? new Date(event.ticket_deadline) : null;
  const deadlinePassed = ticketDeadlineDate && ticketDeadlineDate < new Date();
  const deadlineFormatted = ticketDeadlineDate ? ticketDeadlineDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const bannerImage = event.custom_image || event.cover_image;

  const selectedTierData = tiers.find(t => t.id === selectedTier);
  const tierPrice = selectedTierData ? Number(selectedTierData.price) : 0;
  const subtotal = tierPrice * quantity;
  const buyerFee = tierPrice > 0 ? (3 * quantity) : 0;
  const commission = subtotal > 0 ? (subtotal * 0.03) : 0;
  const totalWithFee = subtotal > 0 ? (subtotal + buyerFee) : 0;

  const handleBuy = async () => {
    if (!selectedTierData || !buyer.email) return;
    if (deadlinePassed) {
      setError('Ticket sales deadline has passed. Purchase is no longer available.');
      return;
    }
    setSubmitting(true);
    try {
      if (tierPrice <= 0) {
        // Free ticket — create directly (no payment needed)
        const verifyRes = await api.post('/tickets/free', {
          tier_id: selectedTier,
          buyer_name: buyer.name || '',
          buyer_email: buyer.email,
          buyer_phone: buyer.phone || '',
          invitation_id: event.invitation_id,
          quantity,
        });
        const codes = verifyRes.data.ticket_codes || [verifyRes.data.ticket_code];
        setPurchaseEventTitle(verifyRes.data.event_title || event?.title || '');
        const tierName = verifyRes.data.tier_name || selectedTierData.name;
        const guestName = verifyRes.data.guest_name || buyer.name || 'Guest';
        setPurchasedTickets(codes.map(code => ({
          ticket_code: code,
          tier_name: tierName,
          price_paid: 0,
          guest_name: guestName
        })));
        setStep('success');
        setSubmitting(false);
        // Auto-RSVP the ticket buyer
        api.post(`/invite/${shareLink}/rsvp`, {
          name: buyer.name || '',
          email: buyer.email,
          phone: buyer.phone || ''
        }).catch(() => {});
        return;
      }

      // Paid ticket — Ozow EFT (the only payment gateway)
      const ozowRes = await api.post('/ozow/ticket/initialize', {
        event_id: event.id,
        tier_id: selectedTier,
        quantity,
        buyer_name: buyer.name || '',
        buyer_email: buyer.email,
        buyer_phone: buyer.phone || '',
      });
      if (ozowRes.data?.success && ozowRes.data?.url && ozowRes.data?.params) {
        // Build and submit hidden form to pay.ozow.com
        const f = document.createElement('form');
        f.method = 'POST';
        f.action = ozowRes.data.url;
        f.style.display = 'none';
        Object.entries(ozowRes.data.params).forEach(([k, v]) => {
          const inp = document.createElement('input');
          inp.type = 'hidden'; inp.name = k; inp.value = v;
          f.appendChild(inp);
        });
        document.body.appendChild(f);
        // Save pending info for verification on return
        sessionStorage.setItem('ggr_ozow_ticket_pending', JSON.stringify({
          reference: ozowRes.data.reference,
          tier_id: selectedTier,
          buyer_name: buyer.name || '',
          buyer_email: buyer.email,
          buyer_phone: buyer.phone || '',
          event_id: event.id,
          quantity,
          invitation_id: event.invitation_id,
        }));
        f.submit();
        return; // Browser will redirect to Ozow
      }
      setError(ozowRes.data?.error || 'Failed to initialize payment. Please try again.');
    } catch (err) {
      if (err.response?.data?.deadline_passed) {
        setError('Ticket sales deadline has passed. Purchase is no longer available.');
        setStep('select');
        setSelectedTier(null);
      } else {
        setError(err.response?.data?.error || 'Payment failed. Please try again.');
      }
    }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, fontFamily: 'Inter, sans-serif', padding: '24px' }}>
      {/* Banner overlay */}
      {bannerImage && <div style={{ position: 'fixed', inset: 0, backgroundImage: `url(${bannerImage})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: isLight ? 0.06 : 0.1, filter: 'blur(3px)', pointerEvents: 'none' }} />}

      <div style={{ position: 'relative', maxWidth: '540px', margin: '0 auto' }}>
        {/* Event header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '3px', color: colors.accent, fontWeight: 700, marginBottom: '8px' }}>Get Your Tickets</div>
          <h1 style={{ fontSize: '32px', fontWeight: 700, color: colors.text, fontFamily: "'Playfair Display', serif", lineHeight: 1.2, marginBottom: '16px' }}>{event.title}</h1>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
            {eventDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: colors.accent, fontSize: '14px' }}>
                <FiCalendar size={14} />
                {eventDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
            {event.location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: colors.accent, fontSize: '14px' }}>
                <FiMapPin size={14} />
                {event.location}
              </div>
            )}
            {event.host_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: colors.accent, fontSize: '14px' }}>
                <FiUser size={14} />
                Hosted by {event.host_name}
              </div>
            )}
          </div>
        </div>

        {/* Step indicator */}
        {step !== 'success' && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
          {['select', 'details', 'confirm'].map((s, i) => (
            <React.Fragment key={s}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step === s ? colors.accent : 'transparent',
                border: `2px solid ${step === s ? colors.accent : `${colors.accent}40`}`,
                color: step === s ? (isLightColor(colors.accent) ? '#1A1A2E' : '#fff') : colors.accent,
                fontSize: '14px', fontWeight: 700
              }}>{i + 1}</div>
              {i < 2 && <div style={{ width: '40px', height: '2px', background: `${colors.accent}30`, marginTop: '15px' }} />}
            </React.Fragment>
          ))}
        </div>
        )}

        {/* Deadline passed notice */}
        {deadlinePassed && (
          <div style={{ marginBottom: '20px', padding: '18px 20px', borderRadius: '14px', background: '#ff444415', border: '1px solid #ff444430', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📅</div>
            <p style={{ fontWeight: 700, fontSize: '16px', color: '#ff6b6b', fontFamily: 'Inter, sans-serif', margin: '0 0 4px' }}>Ticket Sales Have Closed</p>
            <p style={{ fontSize: '13px', color: colors.text, opacity: 0.7, fontFamily: 'Inter, sans-serif', margin: 0 }}>
              The ticket purchase deadline was {deadlineFormatted}. Please contact the event host if you have questions.
            </p>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div style={{ marginBottom: '20px', padding: '14px 18px', borderRadius: '12px', background: '#ff444415', border: '1px solid #ff444430', color: '#ff6b6b', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiInfo size={16} />{error}
            <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '16px' }}>&times;</button>
          </div>
        )}

        {/* Step 1: Select Tier */}
        {step === 'select' && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tiers.map(tier => {
                const isFree = Number(tier.price) <= 0;
                const isSelected = selectedTier === tier.id;
                const isSoldOut = tier.sold_out || deadlinePassed;
                const isDisabled = isSoldOut;
                return (
                  <button
                    key={tier.id}
                    disabled={isDisabled}
                    onClick={() => { if (!deadlinePassed) { setSelectedTier(tier.id); setStep('details'); } }}
                    style={{
                      width: '100%', padding: '20px', borderRadius: '16px', cursor: isDisabled ? 'not-allowed' : 'pointer',
                      background: isSelected ? `${tier.color}18` : 'rgba(255,255,255,0.04)',
                      border: `2px solid ${isSelected ? tier.color : isDisabled ? 'transparent' : 'rgba(255,255,255,0.08)'}`,
                      textAlign: 'left', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s',
                      opacity: isDisabled ? 0.4 : 1
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '4px', background: tier.color, flexShrink: 0 }} />
                          <span style={{ fontSize: '18px', fontWeight: 700, color: colors.text }}>{tier.name}</span>
                        </div>
                        {tier.description && <p style={{ fontSize: '13px', color: colors.text, opacity: 0.6, margin: '4px 0 0' }}>{tier.description}</p>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: colors.accent }}>
                          {isFree ? 'FREE' : formatZAR(tier.price)}
                        </div>
                        {!isDisabled && tier.remaining !== null && (
                          <div style={{ fontSize: '11px', color: colors.text, opacity: 0.5, marginTop: '2px' }}>{tier.remaining} left</div>
                        )}
                        {deadlinePassed && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '2px', fontWeight: 600 }}>Closed</div>
                        )}
                        {!deadlinePassed && isSoldOut && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '2px', fontWeight: 600 }}>Sold Out</div>
                        )}
                      </div>
                    </div>
                    {tier.quantity !== null && !isDisabled && (
                      <div style={{ marginTop: '10px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.08)' }}>
                        <div style={{ height: '100%', borderRadius: '2px', background: tier.color, width: `${Math.min(100, (tier.sold / tier.quantity) * 100)}%` }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Your Details */}
        {step === 'details' && selectedTierData && (
          <div>
            <button onClick={() => { setStep('select'); setSelectedTier(null); }} style={{ background: 'none', border: 'none', color: colors.accent, cursor: 'pointer', fontSize: '14px', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
              &larr; Back to tiers
            </button>

            {/* Selected tier recap */}
            <div style={{ padding: '16px', borderRadius: '12px', background: `${selectedTierData.color}12`, border: `1px solid ${selectedTierData.color}30`, marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: selectedTierData.color }} />
                    <span style={{ fontSize: '16px', fontWeight: 700, color: colors.text }}>{selectedTierData.name}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: colors.text, opacity: 0.5, marginTop: '4px' }}>{tierPrice <= 0 ? 'Free' : formatZAR(tierPrice)} each</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} style={{ width: '28px', height: '28px', borderRadius: '50%', border: `1px solid ${colors.accent}40`, background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</button>
                  <span style={{ fontSize: '16px', fontWeight: 600, color: colors.text, minWidth: '24px', textAlign: 'center' }}>{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: `1px solid ${colors.accent}40`, background: colors.accent, color: isLight ? '#fff' : colors.bg, cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px', display: 'block' }}>Your Name</label>
                <input type="text" value={buyer.name} onChange={e => setBuyer({ ...buyer, name: e.target.value })}
                  placeholder="Enter your full name"
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: colors.text, fontSize: '15px', fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px', display: 'block' }}>Email *</label>
                <input type="email" value={buyer.email} onChange={e => setBuyer({ ...buyer, email: e.target.value })}
                  placeholder="Your email address"
                  required
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: colors.text, fontSize: '15px', fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px', display: 'block' }}>Phone (optional)</label>
                <input type="tel" value={buyer.phone} onChange={e => setBuyer({ ...buyer, phone: e.target.value })}
                  placeholder="e.g. +27 61 391 1942"
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: colors.text, fontSize: '15px', fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>

            <button
              disabled={!buyer.email}
              onClick={() => setStep('confirm')}
              style={{
                width: '100%', marginTop: '24px', padding: '16px', borderRadius: '14px', border: 'none',
                background: !buyer.email ? `${colors.accent}40` : colors.accent,
                color: isLight ? '#fff' : colors.bg,
                fontSize: '16px', fontWeight: 700, cursor: buyer.email ? 'pointer' : 'not-allowed',
                fontFamily: 'Inter, sans-serif'
              }}>
              Continue
            </button>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && selectedTierData && (
          <div>
            <button onClick={() => setStep('details')} style={{ background: 'none', border: 'none', color: colors.accent, cursor: 'pointer', fontSize: '14px', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
              &larr; Back to details
            </button>

            {/* Order summary */}
            <div style={{ padding: '24px', borderRadius: '16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px', color: colors.accent, fontWeight: 600, marginBottom: '20px' }}>Order Summary</h3>

              {/* Buyer info */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, opacity: 0.6, marginBottom: '8px' }}>Attendee</div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>{buyer.name || 'Guest'}</div>
                <div style={{ fontSize: '13px', color: colors.text, opacity: 0.6 }}>{buyer.email}{buyer.phone ? ' / ' + buyer.phone : ''}</div>
              </div>

              {/* Line items */}
              <div style={{ borderTop: `1px solid ${colors.accent}15`, paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: selectedTierData.color }} />
                    <span style={{ fontSize: '14px', color: colors.text }}>{selectedTierData.name} &times; {quantity}</span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>
                    {tierPrice <= 0 ? 'FREE' : formatZAR(subtotal)}
                  </span>
                </div>

                {tierPrice > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: colors.text, opacity: 0.5, fontSize: '13px' }}>
                      <span>Platform fee (3%)</span>
                      <span>{formatZAR(commission)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: colors.text, opacity: 0.5, fontSize: '13px' }}>
                      <span>Booking fee (R3/ticket)</span>
                      <span>{formatZAR(buyerFee)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: `1px solid ${colors.accent}15` }}>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: colors.text }}>Total</span>
                      <span style={{ fontSize: '18px', fontWeight: 700, color: colors.accent }}>{formatZAR(totalWithFee)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payment method — Ozow EFT only */}
            {tierPrice > 0 && (
              <div style={{ padding: '12px 14px', borderRadius: '10px', background: `${colors.accent}08`, border: `1px solid ${colors.accent}12`, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '18px' }}>🏦</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: colors.text }}>Pay with Ozow EFT</div>
                  <p style={{ fontSize: '11px', color: colors.text, opacity: 0.55, margin: '2px 0 0', lineHeight: 1.4 }}>
                    You'll pay by bank transfer (EFT) via Ozow's secure page. No card needed. GGR does not store your bank details.
                  </p>
                </div>
              </div>
            )}

            <button
              disabled={submitting}
              onClick={handleBuy}
              style={{
                width: '100%', padding: '18px', borderRadius: '14px', border: 'none',
                background: submitting ? `${colors.accent}50` : (tierPrice <= 0 ? '#10B981' : colors.accent),
                color: tierPrice <= 0 ? '#fff' : (isLightColor(colors.accent) ? '#1A1A2E' : '#fff'),
                fontSize: '17px', fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
                fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}>
              {submitting ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '18px', height: '18px', border: '2px solid #fff3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  Processing...
                </span>
              ) : tierPrice <= 0 ? 'Get Free Ticket' : `Pay ${formatZAR(totalWithFee)} & Get Ticket`}
            </button>

            {/* Payment logos */}
            <div style={{ textAlign: 'center', marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <img src="/img/payment/ozow-logo.png" alt="Ozow" style={{ height: '22px', opacity: 0.5 }} onError={e => { e.currentTarget.style.display = 'none'; }} />
              <img src="/img/payment/eft-secure.png" alt="Instant EFT" style={{ height: '16px', opacity: 0.4 }} onError={e => { e.currentTarget.style.display = 'none'; }} />
              <span style={{ fontSize: '10px', color: colors.text, opacity: 0.35 }}>Secured by Ozow Instant EFT</span>
            </div>
          </div>
        )}

        {/* Step 4: Purchase Success */}
        {step === 'success' && purchasedTickets.length > 0 && (
          <div>
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
              <h2 style={{ fontSize: '28px', fontWeight: 700, color: colors.text, fontFamily: "'Playfair Display', serif", marginBottom: '8px' }}>
                {purchasedTickets[0].price_paid > 0 ? 'Payment Successful!' : 'Tickets Confirmed!'}
              </h2>
              <p style={{ fontSize: '14px', color: colors.text, opacity: 0.7, fontFamily: 'Inter, sans-serif', lineHeight: 1.5 }}>
                {purchasedTickets.length} ticket{purchasedTickets.length !== 1 ? 's' : ''} for {purchaseEventTitle || (event?.title || 'the event')}.{purchasedTickets[0].price_paid > 0 ? ' Your payment has been received.' : ''} Check your email for details.
              </p>
            </div>

            {/* Purchased tickets list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              {purchasedTickets.map((t, i) => (
                <div key={i} style={{
                  padding: '16px 20px', borderRadius: '14px',
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${selectedTierData?.color || colors.accent}25`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      {t.tier_name && (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                          color: selectedTierData?.color || colors.accent,
                          background: `${selectedTierData?.color || colors.accent}15`,
                          padding: '2px 8px', borderRadius: '12px'
                        }}>{t.tier_name}</span>
                      )}
                      <span style={{ fontSize: '16px', fontWeight: 700, color: colors.text, fontFamily: "'Courier New', monospace" }}>
                        {t.ticket_code}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: colors.text, opacity: 0.6, fontFamily: 'Inter, sans-serif' }}>
                      {t.guest_name}{t.price_paid > 0 ? ` — R${Number(t.price_paid).toFixed(2)}` : ' — Free'}
                    </div>
                  </div>
                  <a href={`/ticket/${t.ticket_code}?share=${encodeURIComponent(event?.share_link || shareLink || '')}`}
                    target="_blank" rel="noreferrer" style={{
                      padding: '8px 16px', borderRadius: '10px', background: colors.accent,
                      color: isLightColor(colors.accent) ? '#1A1A2E' : '#fff', fontSize: '13px', fontWeight: 600,
                      textDecoration: 'none', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap'
                    }}>
                    View Ticket
                  </a>
                </div>
              ))}
            </div>

            {/* Share actions */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
              <button onClick={() => {
                const text = purchasedTickets.map(t =>
                  `Ticket for ${t.guest_name}: ${window.location.origin}/ticket/${t.ticket_code}`
                ).join('\n');
                window.open(`https://wa.me/?text=${encodeURIComponent(`My tickets for ${purchaseEventTitle || event?.title || 'the event'}!\n\n${text}`)}`, '_blank');
              }} style={{
                flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
                background: '#25D366', color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}>
                <FiShare2 size={15} /> Share via WhatsApp
              </button>
              <button onClick={() => {
                const link = purchasedTickets.length === 1
                  ? `${window.location.origin}/ticket/${purchasedTickets[0].ticket_code}`
                  : `${window.location.origin}/ticket/${purchasedTickets[0].ticket_code}`;
                navigator.clipboard?.writeText(link);
                alert('Ticket link copied!');
              }} style={{
                padding: '14px 18px', borderRadius: '12px', border: `1px solid ${colors.accent}30`,
                background: 'transparent', color: colors.text, fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif'
              }}>
                Copy Link
              </button>
            </div>

            {/* View event button */}
            <a href={`/invite/${shareLink}`} style={{
              width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
              background: colors.accent, color: isLightColor(colors.accent) ? '#1A1A2E' : '#fff', fontSize: '14px', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'Inter, sans-serif', textDecoration: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              boxSizing: 'border-box'
            }}>
              <FiCalendar size={15} /> View Event Details
            </a>
          </div>
        )}

        {/* GGR branding */}
        <div style={{ textAlign: 'center', marginTop: '40px', opacity: 0.25 }}>
          <div style={{ fontSize: '16px', color: colors.accent, marginBottom: '6px' }}>✦</div>
          <p style={{ fontSize: '10px', color: colors.text, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Grand Gather Registry</p>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
